import { useEffect, useMemo } from 'react';
import { ActivityIndicator, View } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';

import { Screen } from '@/components/layout/Screen';
import { Button } from '@/components/ui/Button';
import { Body, SubHeading } from '@/components/ui/Text';
import { useTheme } from '@/theme';
import {
  buildTemplatesTabParams,
  normalizeTemplateIdParam,
  normalizeTemplateTopicParam,
} from '@/navigation/templateDeepLink';
import {
  setPendingNavigationIntent,
  toPendingIntentParams,
} from '@/navigation/pendingNavigationIntent';
import { formatTemplateTagLabel } from '@/utils/templateTags';
import { useUserStore } from '@/state/userStore';
import {
  logTemplateDeepLinkAnalytics,
  processAppleSurfaceQueryParams,
} from '@/services/appleDeepLinkSurface';

/**
 * Web + native landing for template campaign links (`/templates?topic=america-250`).
 */
export default function TemplatesDeepLinkScreen() {
  const theme = useTheme();
  const router = useRouter();
  const params = useLocalSearchParams<{
    topic?: string | string[];
    templateId?: string | string[];
    ref?: string | string[];
    utm_source?: string | string[];
    spotlightType?: string | string[];
    spotlightEntityId?: string | string[];
    appIntent?: string | string[];
  }>();

  const topic = normalizeTemplateTopicParam(params.topic);
  const templateId = normalizeTemplateIdParam(params.templateId);
  const ref = Array.isArray(params.ref) ? params.ref[0] : params.ref;
  const utmSource = Array.isArray(params.utm_source) ? params.utm_source[0] : params.utm_source;
  const topicLabel = topic ? formatTemplateTagLabel(topic) : undefined;

  const authReady = useUserStore((s) => s.authReady);
  const isAuthenticated = useUserStore((s) => s.isAuthenticated);
  const hasCompletedOnboarding = useUserStore((s) => s.hasCompletedOnboarding);

  const tabParams = useMemo(
    () =>
      buildTemplatesTabParams({
        topic,
        templateId,
        ref: typeof ref === 'string' ? ref : undefined,
        utmSource: typeof utmSource === 'string' ? utmSource : undefined,
      }),
    [topic, templateId, ref, utmSource],
  );

  useEffect(() => {
    const authState: 'signed_out' | 'signed_in_not_onboarded' | 'signed_in_onboarded' = !isAuthenticated
      ? 'signed_out'
      : hasCompletedOnboarding
        ? 'signed_in_onboarded'
        : 'signed_in_not_onboarded';
    logTemplateDeepLinkAnalytics({
      topic,
      templateId,
      ref: typeof ref === 'string' ? ref : undefined,
      utmSource: typeof utmSource === 'string' ? utmSource : undefined,
      authState,
    });
    processAppleSurfaceQueryParams(params as Record<string, unknown>);
    // We only want first-open attribution for each deep-link entry.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stashIntentAndGoHome = () => {
    setPendingNavigationIntent({
      kind: 'route',
      pathname: '/(tabs)',
      params: toPendingIntentParams(tabParams),
    });
    router.replace('/');
  };

  useEffect(() => {
    if (!authReady || !isAuthenticated || !hasCompletedOnboarding) {
      return;
    }
    router.replace({
      pathname: '/(tabs)',
      params: tabParams,
    } as never);
  }, [authReady, hasCompletedOnboarding, isAuthenticated, router, tabParams]);

  const handleContinueToSignIn = () => {
    setPendingNavigationIntent({
      kind: 'route',
      pathname: '/(tabs)',
      params: toPendingIntentParams(tabParams),
    });
    router.replace('/(onboarding)/sign-in');
  };

  const handleCloseOrHome = () => {
    router.replace('/');
  };

  if (!authReady) {
    return (
      <Screen
        title="Trip templates"
        scrollable={false}
        showOfflineIndicator={false}
        showBackButton
        onBack={handleCloseOrHome}
      >
        <View style={{ alignItems: 'center', justifyContent: 'center', flex: 1, paddingVertical: theme.spacing(3) }}>
          <ActivityIndicator size="large" color={theme.colors.primary} accessibilityLabel="Loading" />
          <Body style={{ marginTop: theme.spacing(2), opacity: 0.85 }}>Loading Tripvio…</Body>
        </View>
      </Screen>
    );
  }

  if (!isAuthenticated) {
    return (
      <Screen
        title="Trip templates"
        showOfflineIndicator={false}
        scrollable={false}
        showBackButton
        onBack={handleCloseOrHome}
      >
        <View style={{ gap: theme.spacing(2) }}>
          <SubHeading>
            {topicLabel ? `Get the ${topicLabel} itinerary` : 'Get your free trip itinerary'}
          </SubHeading>
          <Body style={{ opacity: 0.85 }}>
            Sign up or sign in to open the full hour-by-hour plan in Tripvio—restaurants, budget estimates, and
            shareable schedules included.
          </Body>
          <Button label="Continue" onPress={handleContinueToSignIn} />
          <Button label="Not now" variant="outlined" onPress={stashIntentAndGoHome} />
        </View>
      </Screen>
    );
  }

  if (!hasCompletedOnboarding) {
    return (
      <Screen
        title="Trip templates"
        showOfflineIndicator={false}
        scrollable={false}
        showBackButton
        onBack={handleCloseOrHome}
      >
        <View style={{ gap: theme.spacing(2) }}>
          <SubHeading>Almost there</SubHeading>
          <Body style={{ opacity: 0.85 }}>
            Finish setting up your account and we&apos;ll open the template library for you.
          </Body>
          <Button label="Continue setup" onPress={() => router.replace('/')} />
        </View>
      </Screen>
    );
  }

  return (
    <Screen
      title="Trip templates"
      scrollable={false}
      showOfflineIndicator={false}
      showBackButton
      onBack={handleCloseOrHome}
    >
      <View style={{ alignItems: 'center', justifyContent: 'center', flex: 1, paddingVertical: theme.spacing(3) }}>
        <ActivityIndicator size="large" color={theme.colors.primary} accessibilityLabel="Opening templates" />
        <Body style={{ marginTop: theme.spacing(2), opacity: 0.85 }}>Opening templates…</Body>
      </View>
    </Screen>
  );
}
